import { IconShield } from '@developerz/ui';
import { createEffect, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { createKeyCapture } from '../lib/keyCapture';
import type { AddKeyInput, ProbeModelsInput, ProviderModelOption } from '../lib/keys';
import { AddKeyCredentialStep } from './AddKeyCredentialStep';
import { AddKeyModelsStep } from './AddKeyModelsStep';
import { Modal } from './Modal';

// Add-a-BYOK-key dialog. SHELL only: the two phases are their own presenters and
// `createKeyCapture` is the state machine — capture is credential → probe the
// provider → pick a model + tag tiers → save.
//
// ONE form across both phases, so Enter submits whichever step is showing and
// the browser's own `required` validation still fires; the footer band sits
// INSIDE it and the form is the last template child (happy-dom #481). It stays
// ONE POST too: the enumeration is a read, and the parent still owns the single
// mutation it always owned.
//
// `probe` is a PROP because the two pools authorize differently — the org page
// spends `write:billing` at `/v1/billing/providers/models`, the personal page
// `write:self:keys` at `/v1/me/keys/models`, and a member capturing their own
// key does not hold the admin-only scope.

export type AddKeyModalProps = {
  readonly open: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  /**
   * The pool's pre-capture enumeration, token pre-bound by the route container.
   * Answers ROWS — the provider-reported context window rides each one into the
   * capture body, because nothing downstream can recover a window dropped here.
   */
  readonly probe: (input: ProbeModelsInput) => Promise<readonly ProviderModelOption[]>;
  readonly onSubmit: (input: AddKeyInput) => void;
  readonly onClose: () => void;
};

export function AddKeyModal(props: AddKeyModalProps): JSX.Element {
  // Both deps are read at CALL time, so a prop that changes identity is never
  // captured stale.
  const capture = createKeyCapture({
    probe: (input) => props.probe(input),
    onSubmit: (input) => props.onSubmit(input),
  });

  // Re-arm off the `open` prop, never on unmount: the dialog stays mounted, and
  // a successful capture closes it by flipping `open` rather than through
  // `onClose`. Without this a captured secret — and a model list belonging to
  // the credential it was captured with — would be sitting in the form the next
  // time it opened. (`<SshEnrollPanel/>` holds the same rule for the same reason.)
  createEffect(() => {
    if (!props.open) capture.reset();
  });

  const submit = (e: Event): void => {
    e.preventDefault();
    if (capture.phase() === 'credential') capture.listModels();
    else capture.submit();
  };

  return (
    <Modal
      open={props.open}
      size="lg"
      title={t('account_keys.add_title')}
      desc={t('account_keys.add_desc')}
      onClose={props.onClose}
    >
      <form class="add-key__form" onSubmit={submit}>
        <div class="alert alert--icon" role="alert">
          <IconShield />
          <p class="alert__title">{t('account_keys.encrypted_title')}</p>
          <p class="alert__desc">{t('account_keys.encrypted_desc')}</p>
        </div>
        <Show
          when={capture.phase() === 'credential'}
          fallback={
            <AddKeyModelsStep
              provider={capture.provider()}
              models={capture.models()}
              model={capture.model()}
              onModel={capture.setModel}
              tags={capture.tags}
              busy={props.busy}
              error={props.error}
              onBack={capture.back}
            />
          }
        >
          <AddKeyCredentialStep
            provider={capture.provider()}
            onProvider={capture.setProvider}
            baseUrl={capture.baseUrl()}
            onBaseUrl={capture.setBaseUrl}
            wantsBaseUrl={capture.wantsBaseUrl()}
            needsBaseUrl={capture.needsBaseUrl()}
            baseUrlFault={capture.baseUrlFault()}
            onBaseUrlInvalid={capture.flagBaseUrl}
            secret={capture.key()}
            onSecret={capture.setKey}
            probing={capture.probing()}
            incomplete={capture.credentialIncomplete()}
            refusal={capture.refusal()}
            onCancel={props.onClose}
          />
        </Show>
      </form>
    </Modal>
  );
}
